import { forwardRef, useEffect, useRef, useState } from 'react'
import type { Category } from '../../shared/types'

// Type-to-filter combo box over a client's categories. Keyboard-driven:
// - typing filters the active list and opens the dropdown
// - ArrowUp/Down move the highlight WHEN OPEN; when closed they bubble to the
//   grid (row navigation) via gridKeyDown
// - Enter commits the highlighted option (or clears if the field is empty)
// - Escape closes the dropdown and reverts to the committed value

interface Props {
  value: number | null
  categories: Category[] // active + the currently-selected one (even if inactive)
  disabled?: boolean
  onCommit: (categoryId: number | null) => void
  gridKeyDown: (e: React.KeyboardEvent) => void
  registerRef: (el: HTMLInputElement | null) => void
}

const CategoryCombo = forwardRef<HTMLInputElement, Props>(function CategoryCombo(
  { value, categories, disabled, onCommit, gridKeyDown, registerRef },
  _ref,
) {
  const selected = categories.find((c) => c.id === value) ?? null
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const display = open ? query : selected?.name ?? ''
  const filtered = open
    ? categories.filter((c) => c.active && c.name.toLowerCase().includes(query.toLowerCase()))
    : []

  // Typing resets the highlight to the top match; opening without a query
  // highlights the current selection (handled in the open handlers).
  useEffect(() => {
    setHighlight(0)
  }, [query])

  function openMenu() {
    const active = categories.filter((c) => c.active)
    const idx = active.findIndex((c) => c.id === value)
    setHighlight(idx >= 0 ? idx : 0)
    setOpen(true)
  }

  function commit(cat: Category | null) {
    onCommit(cat ? cat.id : null)
    setOpen(false)
    setQuery('')
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (open) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlight((h) => Math.min(h + 1, Math.max(filtered.length - 1, 0)))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlight((h) => Math.max(h - 1, 0))
        return
      }
      if (e.key === 'Enter') {
        e.preventDefault()
        commit(filtered[highlight] ?? null)
        // after committing, let the grid move down as well
        gridKeyDown(e)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setOpen(false)
        setQuery('')
        return
      }
      if (e.key === 'Tab') {
        commit(filtered[highlight] ?? selected)
        gridKeyDown(e)
        return
      }
      // other keys fall through to update query via onChange
      return
    }
    // closed: everything is grid navigation
    gridKeyDown(e)
  }

  return (
    <div className="relative">
      <input
        ref={(el) => {
          inputRef.current = el
          registerRef(el)
        }}
        className="grid-cell w-full bg-transparent px-2 py-1 outline-none disabled:text-slate-400"
        value={display}
        disabled={disabled}
        placeholder="—"
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onClick={openMenu}
        onFocus={() => {
          /* opened on click/typing, not on keyboard focus, so grid nav isn't hijacked */
        }}
        onBlur={() => {
          // commit best match on blur so tabbing away keeps the value
          if (open) commit(filtered[highlight] ?? selected)
        }}
        onKeyDown={handleKeyDown}
        role="combobox"
        aria-expanded={open}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute left-0 top-full z-20 mt-0.5 max-h-48 w-48 overflow-auto rounded-md border border-slate-200 bg-white py-1 text-sm shadow-lg">
          {filtered.map((c, i) => (
            <li key={c.id}>
              <button
                className={
                  'block w-full px-3 py-1 text-left ' + (i === highlight ? 'bg-slate-100' : 'hover:bg-slate-50')
                }
                onMouseDown={(e) => {
                  e.preventDefault()
                  commit(c)
                }}
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
})

export default CategoryCombo
